import * as yup from 'yup';
import { Box, Grid, Typography, Paper, FormControlLabel, Checkbox } from "@material-ui/core";
import TextField from '@material-ui/core/TextField';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { StepProps } from './types';

const schema = yup.object().shape({
  tipoOcorrencia: yup.object().shape({
    automedicacao: yup.boolean(),
    farmacodependencia: yup.boolean(),
    erroTerapeutico: yup.boolean(),
    campanhaVacinacao: yup.boolean(),
  }),
  breveDescricao: yup.string().required('Descrição é obrigatória'),
  prazoManifestacao: yup.object().shape({
    inicioManifestacao: yup.string().required('Data de início é obrigatória'),
    fimManifestacao: yup.string().required('Data de fim é obrigatória'),
  }),
  evolucaoEfeitos: yup.object().shape({
    riscoVida: yup.boolean(),
    motivouHospitalizacao: yup.boolean(),
    prolongouHospitalizacao: yup.boolean(),
    naoRecuperou: yup.boolean(),
    recuperouSemSequelas: yup.boolean(),
    recuperouComSequelas: yup.boolean(),
    desconhecido: yup.boolean(),
    morte: yup.boolean(),
    tratamentoCorrector: yup.boolean(),
    suspendeuMedicamento: yup.boolean(),
    diminuiuDose: yup.boolean(),
    iddasdm: yup.string(),
    irardm: yup.string(),
    malformacaoCongenita: yup.boolean(),
  }),
});

const useStyles = makeStyles((theme: Theme) => 
  createStyles({
    formContainer: { 
      padding: theme.spacing(2)
    },
    paper: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3)
    },
    mainTitle: {
      marginBottom: theme.spacing(3),
      fontWeight: 500
    },
    sectionTitle: {
      marginBottom: theme.spacing(2),
      fontWeight: 500
    },
    checkboxGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    checkboxRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2)
    },
    charCount: {
      textAlign: 'right',
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5)
    }
  })
);

interface FormData {
  tipoOcorrencia: {
    automedicacao: boolean;
    farmacodependencia: boolean;
    erroTerapeutico: boolean;
    campanhaVacinacao: boolean;
  };
  breveDescricao: string;
  prazoManifestacao: {
    inicioManifestacao: string;
    fimManifestacao: string;
  };
  evolucaoEfeitos: {
    riscoVida: boolean;
    motivouHospitalizacao: boolean;
    prolongouHospitalizacao: boolean;
    naoRecuperou: boolean;
    recuperouSemSequelas: boolean;
    recuperouComSequelas: boolean;
    desconhecido: boolean;
    morte: boolean;
    tratamentoCorrector: boolean;
    suspendeuMedicamento: boolean;
    diminuiuDose: boolean;
    iddasdm: string;
    irardm: string;
    malformacaoCongenita: boolean;
  };
}

const getMessages = (formData: any) => {
  const errorMessages: { [key: string]: string } = {};
  
  if (!formData.descricaoEfeito) errorMessages.descricaoEfeito = 'Descrição do efeito indesejado é obrigatória';
  if (!formData.dataInicioEfeito) errorMessages.dataInicioEfeito = 'Data de início do efeito é obrigatória';
  if (!formData.gravidadeEfeito) errorMessages.gravidadeEfeito = 'Gravidade do efeito é obrigatória';
  if (!formData.desfechoEfeito) errorMessages.desfechoEfeito = 'Desfecho do efeito é obrigatório';
  
  return errorMessages;
};

export default function DescricaoEfeitosIndesejados({}: StepProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const reduxData = useSelector((state: any) => state.dadosEfeitosIndesejados);
const [formData, setFormData] = useState<FormData>(reduxData && Object.keys(reduxData).length > 0 ? reduxData : {
    tipoOcorrencia: {
      automedicacao: false,
      farmacodependencia: false,
      erroTerapeutico: false,
      campanhaVacinacao: false
    },
    breveDescricao: '',
    prazoManifestacao: {
      inicioManifestacao: '',
      fimManifestacao: ''
    },
    evolucaoEfeitos: {
      riscoVida: false,
      motivouHospitalizacao: false,
      prolongouHospitalizacao: false,
      naoRecuperou: false,
      recuperouSemSequelas: false,
      recuperouComSequelas: false,
      desconhecido: false,
      morte: false,
      tratamentoCorrector: false,
      suspendeuMedicamento: false,
      diminuiuDose: false,
      iddasdm: 'Não',
      irardm: 'Sim',
      malformacaoCongenita: false
    }
  });

  const handleCheckboxChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      tipoOcorrencia: {
        ...prev.tipoOcorrencia,
        [field]: e.target.checked
      }
    }));
  };

  const handleDescricaoChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      breveDescricao: e.target.value
    }));
  };

  const { control, formState: { errors } } = useFormContext();

  const handleEvolucaoChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      evolucaoEfeitos: {
        ...prev.evolucaoEfeitos,
        [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
      }
    }));
  };

  useEffect(() => {
    dispatch({
      type: 'dadosEfeitosIndesejados',
      payload: formData
    });
  }, [formData, dispatch]);

  return (
    <Box className={classes.formContainer}>

      {/* Tipo de Ocorrência */}
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Tipo de ocorrência:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className={classes.checkboxGroup}>
              <div className={classes.checkboxRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="automedicacao"
                      id="automedicacao"
                      checked={formData.tipoOcorrencia.automedicacao}
                      onChange={handleCheckboxChange('automedicacao')}
                      color="primary"
                    />
                  }
                  label="Automedicação"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="farmacodependencia"
                      id="farmacodependencia"
                      checked={formData.tipoOcorrencia.farmacodependencia}
                      onChange={handleCheckboxChange('farmacodependencia')}
                      color="primary"
                    />
                  }
                  label="Farmacodependência"
                />
              </div>
              
              <div className={classes.checkboxRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="erroTerapeutico"
                      id="erroTerapeutico"
                      checked={formData.tipoOcorrencia.erroTerapeutico}
                      onChange={handleCheckboxChange('erroTerapeutico')}
                      color="primary"
                    />
                  }
                  label="Erro terapêutico"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="campanhaVacinacao"
                      id="campanhaVacinacao"
                      checked={formData.tipoOcorrencia.campanhaVacinacao}
                      onChange={handleCheckboxChange('campanhaVacinacao')}
                      color="primary"
                    />
                  }
                  label="Campanha de vacinação"
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </Paper>

      {/* Breve Descrição */}
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Breve descrição do efeito
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              multiline
              rows={4}
              label="Breve descrição dos efeitos indesejados"
              fullWidth
              name="breveDescricao"
              id="descricaoEfeitos"
              variant="outlined"
              onChange={handleDescricaoChange}
              value={formData.breveDescricao}
            />
            <Typography className={classes.charCount}>
              {formData.breveDescricao.length} caracteres
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Prazo de Manifestação */}
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Prazo de manifestação:
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Controller
              name="prazoManifestacao.inicioManifestacao"
              control={control}
              rules={{ required: 'Data de início é obrigatória' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Data de início da manifestação"
                  type="date"
                  fullWidth
                  id="inicioManifestacao"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  error={!!get(errors, 'prazoManifestacao.inicioManifestacao')}
                  helperText={String(get(errors, 'prazoManifestacao.inicioManifestacao.message') || '')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="prazoManifestacao.fimManifestacao"
              control={control}
              rules={{ required: 'Data de fim é obrigatória' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Data do fim da manifestação"
                  type="date"
                  fullWidth
                  id="fimManifestacao"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  error={!!get(errors, 'prazoManifestacao.fimManifestacao')}
                  helperText={String(get(errors, 'prazoManifestacao.fimManifestacao.message') || '')}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}